<script setup lang="ts">
import { computed, type Component } from "vue";
import { CloudHail, Snowflake, Umbrella } from "@lucide/vue";
import { useUnitConversion } from "@/composables/useUnitConversion";
import type { TemperatureUnit } from "@/stores/settings.store";

const props = defineProps<{
  rain?: number;
  ice?: number;
  snow?: number;
  unit: TemperatureUnit;
  wallMode?: boolean;
}>();

const { convertPrecipitation, labels } = useUnitConversion(() => props.unit);

interface PrecipitationChip {
  type: "rain" | "ice" | "snow";
  amount: number;
  color: string;
  icon: Component;
}

interface PrecipitationCandidate {
  type: PrecipitationChip["type"];
  amount: number | undefined;
  color: string;
  icon: Component;
}

const chips = computed<PrecipitationChip[]>(() => {
  const candidates: PrecipitationCandidate[] = [
    { type: "rain", amount: props.rain, color: "#00695c", icon: Umbrella },
    { type: "ice", amount: props.ice, color: "#e91e63", icon: CloudHail },
    { type: "snow", amount: props.snow, color: "#03a9f4", icon: Snowflake },
  ];
  return candidates.filter(
    (chip): chip is PrecipitationChip =>
      typeof chip.amount === "number" &&
      Number.isFinite(chip.amount) &&
      chip.amount > 0,
  );
});

/** Format a precipitation amount in the selected unit. */
function formatPrecipitation(value: number, includeUnit = true) {
  const converted = convertPrecipitation(value, 2);
  if (converted === null) return "";
  return includeUnit
    ? `${converted.toFixed(2)} ${labels.value.precipitation}`
    : converted.toFixed(2);
}

/** Increase the chip fill as its precipitation amount rises. */
function chipStyle(value: number, color: string) {
  const alpha = 0.4 + 0.6 * Math.min(value / 0.5, 1);
  return {
    backgroundColor: `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, var(--surface))`,
  };
}
</script>

<template>
  <div
    v-if="chips.length"
    class="period-precip justify-center"
    :class="
      wallMode
        ? 'flex min-h-6 flex-row flex-wrap content-start items-center gap-1'
        : 'flex flex-wrap gap-1.5'
    "
  >
    <div
      v-for="chip in chips"
      :key="chip.type"
      class="precip-chip flex items-center rounded-full font-semibold text-text-primary"
      :class="
        wallMode
          ? 'gap-1 px-1.5 py-0.5 text-sm leading-4 whitespace-nowrap tabular-nums [&>span]:leading-4 [&_svg]:size-3 [&_svg]:shrink-0'
          : 'gap-1 px-2.5 py-1 text-xs leading-normal'
      "
      :style="chipStyle(chip.amount, chip.color)"
    >
      <component :is="chip.icon" class="h-3.5 w-3.5" aria-hidden="true" />
      <span :aria-label="formatPrecipitation(chip.amount)">
        {{ formatPrecipitation(chip.amount, false) }}
      </span>
    </div>
  </div>
</template>
